import Component from '@glimmer/component';
import Checkbox from './checkbox';
import CheckboxGroup from './checkbox-group';
import type { CheckboxSize } from './checkbox';

// ---------------------------------------------------------------------------
// CheckboxList – data-driven multi-checkbox with selectedKeys management
// ---------------------------------------------------------------------------

export interface CheckboxListItem {
  /** Unique key for this option */
  key: string;
  /** Display label */
  label: string;
  /** Optional hint text below the label */
  hint?: string;
  /** Whether this specific option is disabled */
  disabled?: boolean;
}

interface CheckboxListSignature {
  Args: {
    /** Array of items to render as checkboxes */
    items: readonly CheckboxListItem[];
    /** Currently selected keys */
    selectedKeys?: string[];
    /** Called with updated array of selected keys whenever a checkbox changes */
    onChange?: (selectedKeys: string[]) => void;
    /** Group label displayed above the checkboxes */
    label?: string;
    /** Layout direction @default "vertical" */
    orientation?: 'vertical' | 'horizontal';
    /** Size passed to each checkbox @default "sm" */
    size?: CheckboxSize;
    /** Disable all checkboxes */
    isDisabled?: boolean;
    /** Show loading state */
    isLoading?: boolean;
    /** Text shown while loading @default "Loading…" */
    loadingText?: string;
    /** Text shown when items is empty @default "No options available" */
    emptyText?: string;
  };
  Element: HTMLFieldSetElement;
}

/**
 * Reimagined CheckboxList — a data-driven checkbox group.
 *
 * Takes an `@items` array and `@selectedKeys`, manages toggle logic internally,
 * and fires `@onChange` with the full updated keys array.
 *
 * @example
 * ```gts
 * import CheckboxList from 'tio-reimagined/components/checkbox-list';
 *
 * <CheckboxList
 *   @label="Application States"
 *   @items={{this.stateOptions}}
 *   @selectedKeys={{this.selectedStates}}
 *   @onChange={{this.setSelectedStates}}
 *   @size="sm"
 * />
 * ```
 */
export default class CheckboxList extends Component<CheckboxListSignature> {
  private get selectedSet(): Set<string> {
    return new Set(this.args.selectedKeys ?? []);
  }

  private get size(): CheckboxSize {
    return this.args.size ?? 'sm';
  }

  isChecked = (key: string): boolean => {
    return this.selectedSet.has(key);
  };

  toggleItem = (key: string) => {
    return (checked: boolean) => {
      const next = new Set(this.selectedSet);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      this.args.onChange?.([...next]);
    };
  };

  <template>
    <CheckboxGroup
      @label={{@label}}
      @isDisabled={{@isDisabled}}
      @orientation={{@orientation}}
      ...attributes
    >
      {{#if @isLoading}}
        <div class="text-sm text-gray-500">{{if @loadingText @loadingText "Loading…"}}</div>
      {{else if @items.length}}
        {{#each @items as |item|}}
          <Checkbox
            @label={{item.label}}
            @hint={{item.hint}}
            @checked={{this.isChecked item.key}}
            @onChange={{this.toggleItem item.key}}
            @size={{this.size}}
            @isDisabled={{item.disabled}}
          />
        {{/each}}
      {{else}}
        <div class="text-sm text-gray-500">{{if @emptyText @emptyText "No options available"}}</div>
      {{/if}}
    </CheckboxGroup>
  </template>
}

export type { CheckboxListSignature };
